import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PauseIcon } from '$common/icons/pause'
import { PlayIcon } from '$common/icons/play'
import { MediaCard } from '$common/media-card'
import { AppBarBackTitleActionMenu } from '$common/app-bar/app-bar-back-title-action-menu'
import { useFileObjectUrl } from '$func/file'
import { nothing } from '$func/utils'
import { t } from '$i18n'
import { VideoPreviewTypes } from '$comp/_app/components/chat/components/footer/components/send-field/send-field.type'
import { VideoPreviewCompressionSlider } from '$comp/_app/components/chat/components/footer/components/send-field/components/preview-button-bar/video-preview-compression-slider'
import { getVideoDimension } from '$func/video-dimension'
import { ConfirmCancelBox } from '../../preview-button-bar/confirm-cancel-box'

const APPBAR_HEIGHT = 50
const BOTTOM_HEIGHT = 140
interface Props {
  file: File
  onCancel: () => unknown
  videoPreviewMode: VideoPreviewTypes
  setVideoPreviewMode: Dispatch<SetStateAction<VideoPreviewTypes>>
  setVideoCompressionHeight: (rate: number) => unknown
  compressionHeight?: number
}

const marks = [144, 240, 480, 720, 1080]

const StyledIconButton = styled(IconButton)<{ bottomOffset: number }>(
  ({ bottomOffset }) => ({
    position: 'absolute',
    top: `calc(50% - ${25 + bottomOffset}px)`,
    left: 'calc(50% - 25px)',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'rgba(80, 89, 98, 0.7)',
    zIndex: 1,
  }),
)

const StyledVideo = styled(`video`)`
  width: 100%;

  position: relative;
  object-fit: contain;
`

export function VideoPreview({
  file,
  onCancel,
  videoPreviewMode,
  setVideoPreviewMode,
  setVideoCompressionHeight,
  compressionHeight,
}: Props) {
  const url = useFileObjectUrl(file)
  const [marginTop, setMarginTop] = useState(APPBAR_HEIGHT)
  const [offset, setOffset] = useState<number>(0)
  const [maxHeight, setMaxHeight] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef(null)
  const [controllerIsVisible, setControllerIsVisible] = useState(false)

  const [isPlaying, setIsPlaying] = useState(false)
  if (!file) return null

  const handleLoad = useCallback(() => {
    const card = cardRef.current
    const video = videoRef.current
    if (!card || !video) return
    const tempOffset = card.clientHeight - video.clientHeight
    setOffset(tempOffset < 0 ? 0 : tempOffset)
    setLoaded(true)
  }, [])

  const [videoHeight, setVideoHeight] = useState(0)
  useEffect(() => {
    getVideoDimension(file).then(({ height }) => setVideoHeight(height))
  }, [file])

  const [tempCompressionHeight, setTempCompressionHeight] = useState(
    compressionHeight,
  )

  const qualityItems = useMemo(() => {
    if (!videoHeight) return []
    const items = marks.filter(label => label <= videoHeight)
    if (!items.length) return []
    if (!compressionHeight) setTempCompressionHeight(items[items.length - 1])
    return items.map((item, index) => ({
      label: `${item}p`,
      value: index ? (100 / (items.length - 1)) * index : 0,
    }))
  }, [videoHeight, compressionHeight])

  useEffect(() => {
    const margin = offset / 2
    const calculatedMarginTop =
      videoPreviewMode === 'compress'
        ? margin - BOTTOM_HEIGHT > 0
          ? margin - BOTTOM_HEIGHT
          : 0
        : margin

    setMarginTop(calculatedMarginTop)

    const max = cardRef.current?.clientHeight

    const calculatedMaxHeight =
      videoPreviewMode === 'compress' ? max - BOTTOM_HEIGHT : max
    setMaxHeight(calculatedMaxHeight)
  }, [offset, videoPreviewMode, cardRef.current])

  useEffect(() => {
    const supportsVideo = !!videoRef.current.canPlayType(file.type)
    if (supportsVideo) {
      setControllerIsVisible(true)
    }
  }, [])

  const togglePlay = useCallback(() => {
    if (videoRef.current.paused || videoRef.current.ended) {
      videoRef.current.play()?.catch(nothing)
      setIsPlaying(true)
    } else {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [videoRef, controllerRef])

  const onConfirm = async () => {
    setVideoCompressionHeight(tempCompressionHeight)
    setVideoPreviewMode('comment')
  }
  const onReset = () => {
    setVideoPreviewMode('comment')
  }

  return (
    <MediaCard
      sxProps={{
        position: 'fixed',
        top: 0,
      }}
      cardRef={cardRef}
      appbarComponent={
        <AppBarBackTitleActionMenu
          title={t('chat.sendVideo')}
          onBack={onCancel}
        />
      }
    >
      <StyledIconButton
        style={{ display: controllerIsVisible ? 'flex' : 'none' }}
        ref={controllerRef}
        onClick={togglePlay}
        bottomOffset={videoPreviewMode === 'comment' ? 0 : BOTTOM_HEIGHT / 2}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </StyledIconButton>

      <StyledVideo
        id="video-previewer"
        ref={videoRef}
        src={url}
        sx={{
          marginTop: `${marginTop}px`,
          visibility: loaded ? 'visible' : 'hidden',
          maxHeight,
          height: maxHeight,
        }}
        onLoadedData={handleLoad}
      />
      {videoPreviewMode === 'compress' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '140px',
            position: 'absolute',
            bottom: 0,
            width: '100%',
          }}
        >
          <VideoPreviewCompressionSlider
            marks={qualityItems}
            onChange={setTempCompressionHeight}
            value={tempCompressionHeight}
          />

          <ConfirmCancelBox onConfirm={onConfirm} onReset={onReset} />
        </div>
      )}
    </MediaCard>
  )
}
